import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  ClipboardCheck,
  ArrowLeft,
  CheckCircle,
  XCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const AdminApprovalsPage = () => {
  const [approvals, setApprovals] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem("cdace_token");

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/admin/pending-approvals`, { headers: { Authorization: `Bearer ${token}` } });
      setApprovals(response.data);
    } catch (error) {
      if (error.response?.status === 401) navigate("/admin-login");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [token, navigate]);

  const handleApproval = async (type, id, action) => {
    try {
      const endpoint = type === 'timesheet' ? 'timesheets' : type === 'leave' ? 'leave-requests' : 'overtime';
      await axios.put(`${API}/admin/${endpoint}/${id}/approve`, { action }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${type} ${action}d`);
      fetchData();
    } catch (error) {
      toast.error(`Failed to ${action}`);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#0b1c2d] flex items-center justify-center"><div className="text-[#c9a227]">Loading...</div></div>;

  return (
    <div className="min-h-screen bg-[#0b1c2d] lg:ml-64 p-6" data-testid="admin-approvals-page">
      <div className="mb-6">
        <Link to="/admin" className="flex items-center gap-2 text-slate-400 hover:text-white mb-4"><ArrowLeft className="w-4 h-4" />Back to Dashboard</Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Pending Approvals</h1>
            <p className="text-slate-400 mt-1">{approvals?.total_pending || 0} items awaiting approval</p>
          </div>
          <Badge className="bg-red-500/20 text-red-400 text-lg px-4 py-2">{approvals?.total_pending || 0}</Badge>
        </div>
      </div>

      <Tabs defaultValue="timesheets" className="w-full">
        <TabsList className="bg-slate-900/50 border border-slate-700 p-1 mb-6">
          <TabsTrigger value="timesheets" className="data-[state=active]:bg-[#c9a227]/20 data-[state=active]:text-[#c9a227]">
            Timesheets ({approvals?.timesheets?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="leave" className="data-[state=active]:bg-[#c9a227]/20 data-[state=active]:text-[#c9a227]">
            Leave ({approvals?.leave_requests?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="overtime" className="data-[state=active]:bg-[#c9a227]/20 data-[state=active]:text-[#c9a227]">
            Overtime ({approvals?.overtime?.length || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="timesheets">
          <Card className="glass-card">
            <CardContent className="p-0">
              {approvals?.timesheets?.length > 0 ? (
                <table className="data-table">
                  <thead><tr><th>Employee</th><th>Date</th><th>Hours</th><th>Task</th><th>Actions</th></tr></thead>
                  <tbody>
                    {approvals.timesheets.map((ts) => (
                      <tr key={ts.id}>
                        <td className="text-white">{ts.user_name}</td>
                        <td className="text-slate-400">{ts.work_date}</td>
                        <td className="text-[#c9a227] font-semibold">{ts.hours_worked}h</td>
                        <td className="text-slate-400 max-w-[300px] truncate">{ts.task_description}</td>
                        <td>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => handleApproval('timesheet', ts.id, 'approve')} className="bg-green-600 hover:bg-green-700"><CheckCircle className="w-4 h-4 mr-1" />Approve</Button>
                            <Button size="sm" onClick={() => handleApproval('timesheet', ts.id, 'reject')} className="bg-red-600 hover:bg-red-700"><XCircle className="w-4 h-4 mr-1" />Reject</Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-12 text-slate-400"><CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-400" /><p>No pending timesheets</p></div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="leave">
          <Card className="glass-card">
            <CardContent className="p-0">
              {approvals?.leave_requests?.length > 0 ? (
                <table className="data-table">
                  <thead><tr><th>Employee</th><th>Type</th><th>From</th><th>To</th><th>Reason</th><th>Actions</th></tr></thead>
                  <tbody>
                    {approvals.leave_requests.map((lr) => (
                      <tr key={lr.id}>
                        <td className="text-white">{lr.user_name}</td>
                        <td><Badge className="bg-blue-500/20 text-blue-400">{lr.leave_type}</Badge></td>
                        <td className="text-slate-400">{lr.start_date}</td>
                        <td className="text-slate-400">{lr.end_date}</td>
                        <td className="text-slate-400 max-w-[200px] truncate">{lr.reason}</td>
                        <td>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => handleApproval('leave', lr.id, 'approve')} className="bg-green-600 hover:bg-green-700"><CheckCircle className="w-4 h-4 mr-1" />Approve</Button>
                            <Button size="sm" onClick={() => handleApproval('leave', lr.id, 'reject')} className="bg-red-600 hover:bg-red-700"><XCircle className="w-4 h-4 mr-1" />Reject</Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-12 text-slate-400"><CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-400" /><p>No pending leave requests</p></div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="overtime">
          <Card className="glass-card">
            <CardContent className="p-0">
              {approvals?.overtime?.length > 0 ? (
                <table className="data-table">
                  <thead><tr><th>Employee</th><th>Date</th><th>Hours</th><th>Reason</th><th>Actions</th></tr></thead>
                  <tbody>
                    {approvals.overtime.map((ot) => (
                      <tr key={ot.id}>
                        <td className="text-white">{ot.user_name}</td>
                        <td className="text-slate-400">{ot.work_date}</td>
                        <td className="text-[#c9a227] font-semibold">{ot.hours}h @ {ot.overtime_rate}x</td>
                        <td className="text-slate-400 max-w-[200px] truncate">{ot.reason}</td>
                        <td>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => handleApproval('overtime', ot.id, 'approve')} className="bg-green-600 hover:bg-green-700"><CheckCircle className="w-4 h-4 mr-1" />Approve</Button>
                            <Button size="sm" onClick={() => handleApproval('overtime', ot.id, 'reject')} className="bg-red-600 hover:bg-red-700"><XCircle className="w-4 h-4 mr-1" />Reject</Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-12 text-slate-400"><CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-400" /><p>No pending overtime</p></div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminApprovalsPage;
